import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ClipType } from "@air/api/types";
import { Comment, Stack } from "@air/next-icons";
import { getClipAspectRatio } from "@air/utils-clips";
import classNames from "classnames";
import { memo, useMemo } from "react";
import { CardAction } from "~/components/CardAction";
import { AssetGalleryCardMetaDetails } from "~/components/Gallery/AssetGalleryCard/AssetGalleryCardMetaDetails";
import { AssetGalleryCardRightActions } from "~/components/Gallery/AssetGalleryCard/AssetGalleryCardRightActions";
import { AssetGalleryCardShortInfo } from "~/components/Gallery/AssetGalleryCard/AssetGalleryCardShortInfo";
import { AssetGalleryCardThumbnail } from "~/components/Gallery/AssetGalleryCard/AssetGalleryCardThumbnail";
import { CustomFieldsDisplay } from "~/components/Gallery/CustomFieldsDisplay";
import { GalleryCard } from "~/components/Gallery/GalleryCard/GalleryCard";
import { GalleryCardCustomFieldsAction } from "~/components/Gallery/GalleryCardCustomFieldsAction";
import { GalleryItemType } from "~/components/Gallery/types";
import { getAssetCustomFields } from "~/components/Gallery/utils";
import { VideoScrubbableThumbnail } from "~/components/VideoScrubbableThumbnail";
import { ASSET_CARD, ASSET_CARD_TITLE } from "~/constants/testIDs";
import { useVideoScrubbableThumbnail } from "~/hooks/useVideoScrubbableThumbnail";
import { isTitleAndMetadataVisibleSelector, visibleCustomFieldsViewOptionsSelector } from "~/store/configViews/selectors";
import { useAirSelector } from "~/utils/ReduxUtils";
const _AssetGalleryCard = (param)=>{
    let { allCustomFields = [], cardHeight, cardWidth, clip, getItemMenuOptions, isSelectable = true, canViewCustomFields, onAssetClick, onAssetTitleCmdClick, getSelectionMenuOptions, onCustomFieldsClick, onDiscussionsClick, onVersionsClick, showVersionsIndicator, shouldShowDiscussions, getButtonClassName } = param;
    const clipDimensions = useMemo(()=>{
        const aspectRatio = clip.width / clip.height;
        return {
            width: cardWidth,
            height: cardWidth / aspectRatio
        };
    }, [
        cardWidth,
        clip.height,
        clip.width
    ]);
    const { backgroundImageRef, backgroundImageStyles, indicatorRef, onMouseLeave, onMouseMove } = useVideoScrubbableThumbnail({
        aspectRatio: getClipAspectRatio(clip),
        containerDimensions: {
            height: cardHeight,
            width: cardWidth
        }
    });
    const isTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
    const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);
    const customFields = useMemo(()=>getAssetCustomFields({
            allCustomFields,
            clip,
            visibleCustomFields
        }), [
        allCustomFields,
        clip,
        visibleCustomFields
    ]);
    const shouldShowCustomFields = canViewCustomFields && customFields.length > 0;
    const titleLabel = clip.title || clip.importedName || "Untitled";
    const popoverCustomFields = useMemo(()=>{
        var _clip_customFields;
        return clip === null || clip === void 0 ? void 0 : (_clip_customFields = clip.customFields) === null || _clip_customFields === void 0 ? void 0 : _clip_customFields.filter((customField)=>{
            return allCustomFields.some((cf)=>cf.id === customField.id);
        });
    }, [
        clip,
        allCustomFields
    ]);
    return /*#__PURE__*/ _jsx(GalleryCard, {
        testId: ASSET_CARD,
        title: titleLabel,
        getItemMenuOptions: getItemMenuOptions,
        getSelectionMenuOptions: getSelectionMenuOptions,
        item: {
            id: clip.id,
            item: clip,
            type: GalleryItemType.asset
        },
        onClick: onAssetClick,
        onMouseMove: onMouseMove,
        onMouseLeave: onMouseLeave,
        isSelectable: isSelectable,
        getButtonClassName: getButtonClassName,
        children: (param)=>{
            let { setIsMenuOpen, isHovering, isSelected, isHighlighted, isMenuOpen } = param;
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "pointer-events-none relative grow",
                        children: [
                            clip.assets.spriteSheet && clip.type === ClipType.video && isHovering ? /*#__PURE__*/ _jsx(VideoScrubbableThumbnail, {
                                backgroundImageRef: backgroundImageRef,
                                indicatorRef: indicatorRef,
                                src: clip.assets.spriteSheet,
                                style: backgroundImageStyles
                            }) : /*#__PURE__*/ _jsx(AssetGalleryCardThumbnail, {
                                clip: clip,
                                isHovering: isHovering,
                                isSelected: isSelected || isHighlighted,
                                width: clipDimensions.width,
                                height: clipDimensions.height
                            }),
                            !isTitleAndMetadataVisible && /*#__PURE__*/ _jsx("div", {
                                className: classNames("absolute bottom-0 left-0 right-0 flex h-[100px] flex-col justify-end rounded rounded-t-none bg-gradient-to-b from-black/0 to-black/90 px-2 pb-2 pt-1", isHovering ? "opacity-100" : "opacity-0"),
                                children: /*#__PURE__*/ _jsx(AssetGalleryCardMetaDetails, {
                                    hasDarkBackground: true,
                                    clip: clip,
                                    title: titleLabel,
                                    onTitleClick: onAssetClick,
                                    onTitleCmdClick: onAssetTitleCmdClick,
                                    /**
                   * The width is equal to the card width minus the padding on the left and right
                   */ width: cardWidth - 16,
                                    titleTestId: ASSET_CARD_TITLE
                                })
                            }),
                            (isTitleAndMetadataVisible || !isHovering && !isMenuOpen) && /*#__PURE__*/ _jsx("div", {
                                className: "absolute bottom-2 right-2 z-10",
                                children: /*#__PURE__*/ _jsx(AssetGalleryCardShortInfo, {
                                    clip: clip
                                })
                            }),
                            !!clip.relevance && /*#__PURE__*/ _jsx("div", {
                                className: "absolute bottom-2 right-2 z-10",
                                children: /*#__PURE__*/ _jsx("span", {
                                    className: "rounded bg-white px-1.5 py-0.5 text-12 text-grey-10",
                                    children: clip.relevance.toFixed(5)
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "pointer-events-none absolute left-2 top-2 flex gap-1",
                                children: [
                                    !!(popoverCustomFields === null || popoverCustomFields === void 0 ? void 0 : popoverCustomFields.length) && canViewCustomFields && /*#__PURE__*/ _jsx(GalleryCardCustomFieldsAction, {
                                        customFields: popoverCustomFields,
                                        onClick: onCustomFieldsClick
                                    }),
                                    showVersionsIndicator && !!onVersionsClick && /*#__PURE__*/ _jsx(CardAction, {
                                        "data-testid": "CARD_VERSIONS_INDICATOR",
                                        icon: /*#__PURE__*/ _jsx(Stack, {
                                            className: "size-4"
                                        }),
                                        onClick: onVersionsClick
                                    }),
                                    shouldShowDiscussions && !!onDiscussionsClick && !!clip.openCommentCount && /*#__PURE__*/ _jsx(CardAction, {
                                        "data-testid": "CARD_DISCUSSIONS_INDICATOR",
                                        icon: /*#__PURE__*/ _jsx(Comment, {
                                            className: "size-4"
                                        }),
                                        onClick: onDiscussionsClick,
                                        text: clip.openCommentCount
                                    })
                                ]
                            }),
                            isHovering && /*#__PURE__*/ _jsx("div", {
                                className: "pointer-events-none absolute right-2 top-2 flex gap-1",
                                children: /*#__PURE__*/ _jsx(AssetGalleryCardRightActions, {
                                    clip: clip,
                                    getItemMenuOptions: getItemMenuOptions,
                                    getSelectionMenuOptions: getSelectionMenuOptions,
                                    setIsMenuOpen: setIsMenuOpen
                                })
                            })
                        ]
                    }),
                    shouldShowCustomFields || isTitleAndMetadataVisible ? /*#__PURE__*/ _jsxs("div", {
                        className: "pointer-events-none relative mx-0.5 mt-1.5 flex flex-col items-start gap-1",
                        children: [
                            isTitleAndMetadataVisible && /*#__PURE__*/ _jsx(AssetGalleryCardMetaDetails, {
                                clip: clip,
                                title: titleLabel,
                                onTitleClick: onAssetClick,
                                /** Card width minus the left and right padding */ width: cardWidth - 4,
                                titleTestId: ASSET_CARD_TITLE
                            }),
                            shouldShowCustomFields && /*#__PURE__*/ _jsx(CustomFieldsDisplay, {
                                className: classNames("px-1", isTitleAndMetadataVisible ? "mt-0.5" : undefined),
                                customFields: customFields,
                                onClick: onCustomFieldsClick
                            })
                        ]
                    }) : null
                ]
            });
        }
    });
};
export const AssetGalleryCard = /*#__PURE__*/ memo(_AssetGalleryCard);
